import { useEffect, useState } from 'react';
import { X, Loader2, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

// In-app NPS prompt. Shows bottom-right for logged-in vendors when the backend
// says it's time to ask (account >= 3 days old, no feedback in 90 days) and the
// user hasn't snoozed it locally. Score 0–10 + optional comment.

const SNOOZE_KEY = 'brix_nps_snooze_until';
const SNOOZE_DAYS = 14;

export function NpsWidget() {
  const [show, setShow] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozed > Date.now()) return;
    api.get('/feedback/status')
      .then(({ data }) => { if (data?.shouldAsk) setShow(true); })
      .catch(() => {});
  }, []);

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86_400_000));
    setShow(false);
  };

  const submit = async () => {
    if (score == null) return;
    setSubmitting(true);
    try {
      await api.post('/feedback', { score, comment: comment.trim() || undefined });
      // Backend won't re-ask for 90 days; snooze locally too.
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + 90 * 86_400_000));
      setDone(true);
      setTimeout(() => setShow(false), 2200);
    } catch {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[min(92vw,360px)] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 animate-in slide-in-from-bottom-4 duration-300">
      <button onClick={snooze} className="absolute top-3 right-3 text-[#94A3B8] hover:text-[#64748B]" aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>

      {done ? (
        <div className="flex flex-col items-center text-center py-4">
          <CheckCircle className="h-8 w-8 text-emerald-500 mb-2" />
          <p className="text-sm font-semibold text-slate-800">Thank you! 🙏</p>
          <p className="text-xs text-slate-500 mt-1">Your feedback helps us improve.</p>
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold text-[#1E293B] pr-6">How likely are you to recommend BrixLead to a colleague?</p>

          {/* 0–10 scale */}
          <div className="mt-3 grid grid-cols-11 gap-1">
            {Array.from({ length: 11 }, (_, n) => (
              <button
                key={n}
                onClick={() => setScore(n)}
                className={`h-8 rounded-md text-xs font-semibold transition-all ${
                  score === n
                    ? 'bg-[#6366F1] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1 px-0.5">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>

          {score != null && (
            <>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={2}
                placeholder="What's the main reason for your score? (optional)"
                className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent resize-none"
              />
              <button
                onClick={submit}
                disabled={submitting}
                className="mt-3 w-full h-10 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
